
import React, { useState, useEffect } from "react";
import { usePartners } from "@/context/PartnerContext";
import { useLeads } from "@/context/LeadContext";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { ChevronLeft, ChevronRight, Download, RefreshCw, Search, Users, FileBarChart, Filter, TrendingUp, Calendar, AlertCircle } from "lucide-react";
import { format, sub, startOfMonth, endOfMonth } from "date-fns";
import { el } from "date-fns/locale";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";

interface PartnerStatistics {
  id: string;
  name: string;
  totalLeads: number;
  closedLeads: number;
  pendingLeads: number;
  infoRequests: number;
  conversionRate: number;
  lastLead: string | null;
  viewRate: number;
}

const PartnerReports = () => {
  const { partners, refreshPartners } = usePartners();
  const { leads } = useLeads();
  const [statistics, setStatistics] = useState<PartnerStatistics[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateRange, setDateRange] = useState<"all" | "month" | "quarter" | "year">("month");
  const [isLoading, setIsLoading] = useState(true);

  // For pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    calculateStatistics();
  }, [partners, leads, dateRange]);

  const calculateStatistics = () => {
    setIsLoading(true);

    // Calculate date range filter
    const now = new Date();
    let startDate: Date;

    switch (dateRange) {
      case "month":
        startDate = startOfMonth(now);
        break;
      case "quarter":
        startDate = sub(now, { months: 3 });
        break;
      case "year":
        startDate = sub(now, { years: 1 });
        break;
      default:
        startDate = new Date(0); // Beginning of time for "all"
    }

    // Filter leads by date range
    const filteredLeads = leads.filter(lead =>
      new Date(lead.createdAt) >= startDate
    );

    const stats = partners.map(partner => {
      // Get all leads assigned to this partner
      const partnerLeads = filteredLeads.filter(lead => lead.partnerId === partner.id);

      // Count leads by stage
      const closedLeads = partnerLeads.filter(lead => lead.stage === "έκλεισεΗΔουλειά").length;
      const pendingLeads = partnerLeads.filter(lead => lead.stage === "σεΕκκρεμότητα").length;
      const infoRequests = partnerLeads.filter(lead => lead.stage === "ήθελεΠληροφορίες").length;

      // Calculate view rate
      const viewedLeads = partnerLeads.filter(lead => lead.viewed).length;
      const viewRate = partnerLeads.length > 0
        ? (viewedLeads / partnerLeads.length) * 100
        : 0;

      // Find last lead date
      let lastLead: string | null = null;
      if (partnerLeads.length > 0) {
        // Sort by date descending and take the first one
        const sortedLeads = [...partnerLeads].sort((a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
        lastLead = sortedLeads[0].createdAt;
      }

      // Calculate conversion rate
      const conversionRate = partnerLeads.length > 0
        ? (closedLeads / partnerLeads.length) * 100
        : 0;

      return {
        id: partner.id,
        name: partner.name,
        totalLeads: partnerLeads.length,
        closedLeads,
        pendingLeads,
        infoRequests,
        conversionRate,
        lastLead,
        viewRate
      };
    });

    setStatistics(stats);
    setIsLoading(false);
  };

  const handleRefresh = async () => {
    setIsLoading(true);
    try {
      await refreshPartners();
      calculateStatistics();
    } catch (error) {
      console.error("Error refreshing data:", error);
    }
  };

  // Filter statistics by search term
  const filteredStatistics = statistics.filter(stat =>
    stat.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Sort by total leads (descending)
  const sortedStatistics = [...filteredStatistics].sort((a, b) =>
    b.totalLeads - a.totalLeads
  );

  // Pagination
  const totalPages = Math.ceil(sortedStatistics.length / itemsPerPage);
  const currentData = sortedStatistics.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "Καμία";
    return format(new Date(dateString), "dd/MM/yyyy", { locale: el });
  };

  const handleExport = () => {
    // Format data for CSV
    const headers = "Συνεργάτης,Σύνολο Επαφών,Ολοκληρώθηκαν,Εκκρεμούν,Ερωτήσεις,Ποσοστό Μετατροπής,Τελευταία Επαφή,Ποσοστό Προβολών\n";

    const rows = sortedStatistics.map(stat => {
      return [
        stat.name,
        stat.totalLeads,
        stat.closedLeads,
        stat.pendingLeads,
        stat.infoRequests,
        `${stat.conversionRate.toFixed(1)}%`,
        formatDate(stat.lastLead),
        `${stat.viewRate.toFixed(1)}%`
      ].join(",");
    }).join("\n");

    const csvContent = `data:text/csv;charset=utf-8,${headers}${rows}`;
    const encodedUri = encodeURI(csvContent);

    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `συνεργάτες-αναφορά-${format(new Date(), "yyyy-MM-dd")}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getDateRangeLabel = () => {
    const now = new Date();
    switch (dateRange) {
      case "month":
        return `${format(startOfMonth(now), "MMMM yyyy", { locale: el })}`;
      case "quarter":
        return `${format(sub(now, { months: 3 }), "dd/MM/yyyy", { locale: el })} - ${format(now, "dd/MM/yyyy", { locale: el })}`;
      case "year":
        return `${format(sub(now, { years: 1 }), "dd/MM/yyyy", { locale: el })} - ${format(now, "dd/MM/yyyy", { locale: el })}`;
      default:
        return "Όλες οι περίοδοι";
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 shadow-lg">
            <FileBarChart className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">Αναφορές Συνεργατών</h1>
            <p className="text-slate-500 mt-0.5">Αναλυτικά στατιστικά απόδοσης του δικτύου συνεργατών</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={isLoading}
            className="border-slate-200"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Ανανέωση
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
            className="border-slate-200"
          >
            <Download className="h-4 w-4 mr-2" />
            Εξαγωγή CSV
          </Button>
        </div>
      </div>

      {/* Filter Card */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="pb-3 border-b border-slate-100 bg-slate-50/50">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-slate-500" />
            <CardTitle className="text-base font-medium text-slate-700">Φίλτρα Αναζήτησης</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Αναζήτηση Συνεργάτη</label>
              <div className="relative">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="π.χ. Όνομα συνεργάτη..."
                  className="pl-9 border-slate-200 focus:border-indigo-400 focus:ring-indigo-400"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Χρονική Περίοδος</label>
              <Select value={dateRange} onValueChange={(value: "all" | "month" | "quarter" | "year") => setDateRange(value)}>
                <SelectTrigger className="border-slate-200">
                  <SelectValue placeholder="Επιλέξτε περίοδο" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Όλο το διάστημα</SelectItem>
                  <SelectItem value="month">Τρέχων μήνας</SelectItem>
                  <SelectItem value="quarter">Τελευταίο τρίμηνο</SelectItem>
                  <SelectItem value="year">Τελευταίο έτος</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-slate-200 shadow-sm hover:shadow-md transition-shadow">
          <CardContent className="p-6">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm font-medium text-slate-500 mb-1">Συνεργάτες</p>
                <h3 className="text-3xl font-bold text-slate-900">{partners.filter(p => p.active).length}</h3>
                <p className="text-xs text-emerald-600 font-medium mt-1">Ενεργοί στο δίκτυο</p>
              </div>
              <div className="h-10 w-10 rounded-lg bg-indigo-50 flex items-center justify-center">
                <Users className="h-5 w-5 text-indigo-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm hover:shadow-md transition-shadow">
          <CardContent className="p-6">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm font-medium text-slate-500 mb-1">Σύνολο Επαφών</p>
                <h3 className="text-3xl font-bold text-slate-900">
                  {statistics.reduce((sum, stat) => sum + stat.totalLeads, 0)}
                </h3>
                <p className="text-xs text-slate-400 mt-1">{getDateRangeLabel()}</p>
              </div>
              <div className="h-10 w-10 rounded-lg bg-blue-50 flex items-center justify-center">
                <Users className="h-5 w-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm hover:shadow-md transition-shadow">
          <CardContent className="p-6">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm font-medium text-slate-500 mb-1">Ολοκληρωμένες</p>
                <h3 className="text-3xl font-bold text-slate-900">
                  {statistics.reduce((sum, stat) => sum + stat.closedLeads, 0)}
                </h3>
                <div className="flex items-center gap-1 mt-1">
                  <div className="h-1.5 w-1.5 rounded-full bg-emerald-500"></div>
                  <p className="text-xs text-emerald-600 font-medium">Επιτυχημένες πωλήσεις</p>
                </div>
              </div>
              <div className="h-10 w-10 rounded-lg bg-emerald-50 flex items-center justify-center">
                <TrendingUp className="h-5 w-5 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm hover:shadow-md transition-shadow">
          <CardContent className="p-6">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm font-medium text-slate-500 mb-1">Μ.Ο. Μετατροπής</p>
                <h3 className="text-3xl font-bold text-slate-900">
                  {statistics.length > 0
                    ? (statistics.reduce((sum, stat) => sum + stat.conversionRate, 0) / statistics.length).toFixed(1) + '%'
                    : '0%'
                  }
                </h3>
                <p className="text-xs text-slate-400 mt-1">Για την επιλεγμένη περίοδο</p>
              </div>
              <div className="h-10 w-10 rounded-lg bg-amber-50 flex items-center justify-center">
                <AlertCircle className="h-5 w-5 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Statistics Table */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="px-6 py-4 border-b border-slate-100 bg-white">
          <CardTitle className="text-lg text-slate-800">Αναλυτικά Στατιστικά Συνεργατών</CardTitle>
          <CardDescription>Κατάταξη και επιδόσεις συνεργατών</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-12 text-slate-500">
              <RefreshCw className="animate-spin h-8 w-8 text-indigo-500 mb-3" />
              <p>Υπολογισμός στατιστικών...</p>
            </div>
          ) : currentData.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-slate-500 bg-slate-50/50">
              <fileBarChart className="h-10 w-10 text-slate-300 mb-3" />
              <p>Δεν βρέθηκαν συνεργάτες που να ταιριάζουν με τα κριτήρια αναζήτησης.</p>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader className="bg-slate-50">
                  <TableRow>
                    <TableHead className="font-semibold text-slate-700 pl-6">Συνεργάτης</TableHead>
                    <TableHead className="text-right font-semibold text-slate-700">Σύνολο Επαφών</TableHead>
                    <TableHead className="text-right font-semibold text-slate-700"><span className="text-emerald-600">Ολοκληρώθηκαν</span></TableHead>
                    <TableHead className="text-right font-semibold text-slate-700">Εκκρεμούν</TableHead>
                    <TableHead className="text-right font-semibold text-slate-700">Ερωτήσεις</TableHead>
                    <TableHead className="text-right font-semibold text-slate-700">Μετατροπή</TableHead>
                    <TableHead className="font-semibold text-slate-700">Τελευταία Επαφή</TableHead>
                    <TableHead className="text-right font-semibold text-slate-700">Προβολές</TableHead>
                    <TableHead className="font-semibold text-slate-700 pr-6">Ενέργειες</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {currentData.map((stat) => (
                    <TableRow key={stat.id} className="hover:bg-slate-50/50 transition-colors">
                      <TableCell className="font-medium text-slate-900 pl-6">{stat.name}</TableCell>
                      <TableCell className="text-right font-medium">{stat.totalLeads}</TableCell>
                      <TableCell className="text-right font-bold text-emerald-600 bg-emerald-50/30">{stat.closedLeads}</TableCell>
                      <TableCell className="text-right text-amber-600">{stat.pendingLeads}</TableCell>
                      <TableCell className="text-right text-slate-500">{stat.infoRequests}</TableCell>
                      <TableCell className="text-right">
                        <Badge
                          variant="outline"
                          className={`${stat.conversionRate > 30 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                              stat.conversionRate > 15 ? 'bg-amber-50 text-amber-700 border-amber-200' :
                                'bg-slate-50 text-slate-600 border-slate-200'
                            }`}
                        >
                          {stat.conversionRate.toFixed(1)}%
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-slate-500">
                        <div className="flex items-center gap-1.5">
                          <Calendar className="h-3 w-3" />
                          {formatDate(stat.lastLead)}
                        </div>
                      </TableCell>
                      <TableCell className="text-right text-slate-600">{stat.viewRate.toFixed(1)}%</TableCell>
                      <TableCell className="pr-6">
                        <Link to={`/admin/partners/${stat.id}/dashboard`}>
                          <Button size="sm" variant="ghost" className="h-8 text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50">
                            Λεπτομέρειες
                          </Button>
                        </Link>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100 bg-slate-50/30">
                  <div className="text-xs text-slate-500 font-medium">
                    Σελίδα {currentPage} από {totalPages}
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="h-8 w-8 p-0"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      className="h-8 w-8 p-0"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PartnerReports;
